/**
 * @lodex-pro/shared/auth/amplify-config — buildAmplifyConfig + selectPoolByTenantType.
 *
 * Phase 4 / Plan 03 — D-05, RESEARCH § Pattern 4. Pure function mapping a
 * single pool config entry (one element of the JSON array Phase 3's
 * `loadSecret('COGNITO_POOL_CONFIG_JSON')` returns) into Amplify v6's
 * `Amplify.configure(...)` shape.
 *
 * NOTE: Amplify.configure accepts EXACTLY ONE Cognito pool. Mobile + each web
 * surface select the right pool from the loaded array based on the user's
 * tenant type. The shared package intentionally does NOT import
 * `@aws-amplify/*` (kept cross-runtime safe per `packages/shared/src/index.ts`
 * lines 1-12 — no Node / browser / RN runtime deps); the actual
 * `Amplify.configure(...)` peer-import happens in mobile/web bootstrap, which
 * passes the literal returned by `buildAmplifyConfig`.
 *
 * The server-side composite verifier (Phase 3 D-01) accepts tokens from any
 * of the 4 pools — client-side single-pool selection is for SignIn/MFA flow
 * routing only. T-04-03-03 (accept disposition): wrong-pool selection on the
 * client surfaces as `NotAuthorizedException` at Cognito SignIn; the cure is
 * the tenant-context resolution in plans 04-07 / 04-09.
 *
 * Spec: CONTEXT D-05, master spec §7, RESEARCH § Pattern 4 lines 350-381,
 *       RESEARCH § Standard Stack lines 117-130 (Amplify v6 versions).
 */

/**
 * One pool entry as stored in AWS Secrets Manager at
 * `lodex/cognito/{env}/pool-config`. The CDK Cognito stack writes the JSON
 * array; Phase 3's `loadSecret('COGNITO_POOL_CONFIG_JSON')` reads it.
 *
 * Field shape matches `packages/api/src/auth/cognito-verifier.ts` PoolConfig
 * + the CDK stack's `poolConfigs.push({ userPoolId, clientId, tokenUse })`
 * loop at `infrastructure/aws-cdk/lib/cognito-stack.ts` lines 147-151.
 */
export interface PoolConfigEntry {
  readonly userPoolId: string;
  readonly clientId: string;
  /** Always `'access'` — D-02 forbids id_token consumption server-side. */
  readonly tokenUse: 'access';
}

/**
 * The Amplify v6 `ResourcesConfig` subset lodeX Pro consumes. Inlined here
 * (no Amplify type import) so this module stays cross-runtime safe.
 *
 * The full Amplify type is broader; we intentionally pin only the keys we
 * write so a type-mismatch upstream becomes a compile error at the call site
 * rather than a silent shape drift.
 */
export interface AmplifyAuthConfig {
  readonly Auth: {
    readonly Cognito: {
      readonly userPoolId: string;
      readonly userPoolClientId: string;
      readonly signUpVerificationMethod: 'code';
      readonly loginWith: { readonly email: true; readonly phone: true };
      readonly mfa: {
        readonly status: 'optional';
        readonly smsEnabled: true;
        readonly totpEnabled: true;
      };
    };
  };
}

/**
 * The 4 tenant-type discriminator values in **CDK iteration order** —
 * `infrastructure/aws-cdk/lib/cognito-stack.ts` line 60:
 *   `const TENANT_TYPES = ['hauler', 'operator', 'disposal', 'platform'] as const;`
 *
 * Lower-case (not the upper-case `TENANT_TYPES` from
 * `@lodex-pro/shared/db/schema/enums`): the CDK stack writes the secret with
 * pools serialized in this positional order, so `selectPoolByTenantType`
 * indexes into the loaded JSON array using these labels.
 *
 * If the CDK loop's iteration order ever changes, this const + the
 * `selectPoolByTenantType` index map must change together.
 */
export const AMPLIFY_TENANT_TYPE_ORDER = ['hauler', 'operator', 'disposal', 'platform'] as const;

export type AmplifyTenantType = (typeof AMPLIFY_TENANT_TYPE_ORDER)[number];

/**
 * Pure function: map one `PoolConfigEntry` into the Amplify v6
 * `Amplify.configure(...)` shape. No side effects. The mobile/web bootstrap
 * file is responsible for the actual `Amplify.configure(buildAmplifyConfig(p))`
 * call.
 *
 * `signUpVerificationMethod: 'code'`, `loginWith: { email, phone }`, and
 * `mfa: OPTIONAL with SMS+TOTP` mirror the CDK pool config at
 * `infrastructure/aws-cdk/lib/cognito-stack.ts` lines 95-104 verbatim — so
 * the Cognito-side capability and the Amplify-side declaration never drift.
 *
 * @param pool one entry from the loaded COGNITO_POOL_CONFIG_JSON array
 * @returns the literal object passed to Amplify.configure in mobile/web
 */
export function buildAmplifyConfig(pool: PoolConfigEntry): AmplifyAuthConfig {
  return {
    Auth: {
      Cognito: {
        userPoolId: pool.userPoolId,
        userPoolClientId: pool.clientId,
        signUpVerificationMethod: 'code',
        loginWith: { email: true, phone: true },
        mfa: { status: 'optional', smsEnabled: true, totpEnabled: true },
      },
    },
  };
}

/**
 * Pick the pool entry for a given tenant type from the loaded
 * COGNITO_POOL_CONFIG_JSON array. Relies on the CDK loop's iteration order
 * (`AMPLIFY_TENANT_TYPE_ORDER`) — the array is positional, not keyed.
 *
 * Used by plans 04-07 (personal-device bootstrap reads the driver's resolved
 * tenant type to pick the right pool) and 04-09 (web tenant-picker post-login
 * re-bootstrap on tenant switch).
 *
 * @throws if `pools.length !== AMPLIFY_TENANT_TYPE_ORDER.length` (positional
 *         contract violation — refuse to silently pick the wrong pool).
 */
export function selectPoolByTenantType(
  pools: ReadonlyArray<PoolConfigEntry>,
  tenantType: AmplifyTenantType,
): PoolConfigEntry {
  if (pools.length !== AMPLIFY_TENANT_TYPE_ORDER.length) {
    throw new Error(
      `selectPoolByTenantType: expected ${AMPLIFY_TENANT_TYPE_ORDER.length} pool entries ` +
        `(one per tenant type in CDK iteration order: ${AMPLIFY_TENANT_TYPE_ORDER.join(', ')}), ` +
        `got ${pools.length}. Check COGNITO_POOL_CONFIG_JSON.`,
    );
  }
  const index = AMPLIFY_TENANT_TYPE_ORDER.indexOf(tenantType);
  // Index can't be -1 because tenantType is typed AmplifyTenantType — but the
  // runtime check guards against a caller that widened to string via `as any`.
  if (index < 0) {
    throw new Error(
      `selectPoolByTenantType: unknown tenantType '${String(tenantType)}'. ` +
        `Expected one of: ${AMPLIFY_TENANT_TYPE_ORDER.join(', ')}.`,
    );
  }
  const entry = pools[index];
  if (entry === undefined) {
    // Defensive: length check above should have caught this; satisfies
    // `noUncheckedIndexedAccess` if enabled in tsconfig.
    throw new Error(`selectPoolByTenantType: pool entry at index ${String(index)} is undefined`);
  }
  return entry;
}
